import React, { useCallback, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Button from '../../../../../components/button';
import ServerError from '../../../../../components/ServerError';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import { Redirect } from '../../../../../Router';
import { EmployeeFeedbackQuestionnaire } from '../../../components/EmployeeFeedbackQuestionnaire';
import PageHeader from '../../../components/PageHeader';
import { useUserInfo } from '../../../context/UserInfo';
import { routes } from '../../../routes';
import {
  EmployeeFeedbackState,
  FeedbackCycleState,
  MyPerformanceReviewsDocument,
  UpdateEmployeeFeedbackAnswerInput,
  useSelfPerformanceEvaluationQuery,
  useSubmitSelfPerformanceEvaluationMutation,
} from '../../../__generated__/graphql';
import { visibleForEmployee } from '../EmployeePerformanceReview/EmployeeFeedbacks/utils';
import { GoalsSectionWithContext } from '../EmployeePerformanceReview/Goals2/GoalsSection';
import FeedbackStatusSection from '../EmployeePerformanceReview/EmployeePerformanceReviewCompareFeedback/FeedbackStatusSection';
import { useEmployeeSignAndFinish } from '../helpers';
import { FeedbackCycleIntroductionText } from './FeedbackCycleIntroductionText';
import { LoadingSelfPerformanceEvaluation } from './LoadingSelfPerformanceEvaluation';
import { useFlairBreadcrumbHook } from '../../../../../hooks/useFlairBreadcrumbHook';
import { EmployeeSatisfactionModal } from './EmployeeSatisfactionModal';
import './styles.css';
import ConfirmButton from '../../../../../components/button/ConfirmButton';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { useTranslation } from 'react-i18next';
import { SaveChangesButton } from '../SaveChangesButton';
import { useUnsavedChanges } from '../../../../../hooks/useUnsavedChanges';
import type { GoalResultChange } from '../../../components/EmployeeFeedbackQuestionnaire/InlineGoalQuestion';
import { EmployeeEvaluationInlineGoals } from '../EmployeeEvaluationInlineGoals';
import { useUpdateObjectiveResults } from '../EmployeesObjectiveAchievement/useUpdateObjectiveResults';
import type { GoalsInReviewMode } from '../hooks/useGoalsInReviewMode';

type Params = {
  feedbackId: string;
};

const i18Path = 'employeePage.performanceReview.meeting';

const Content: React.FC<React.PropsWithChildren<Params>> = ({ feedbackId }) => {
  const t = useNamespacedTranslation(i18Path);

  const { t: rootT } = useTranslation();
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [showSatisfactionModal, setShowSatisfactionModal] =
    useState<boolean>(false);
  const { data, loading, error } = useSelfPerformanceEvaluationQuery({
    variables: {
      id: feedbackId,
    },
    fetchPolicy: 'cache-and-network',
  });

  useFlairBreadcrumbHook(
    [
      { label: rootT('navigation.menuItems.personalDevelopment.title') },
      {
        label: rootT('performanceReview.subMenuItems.reviews.title'),
        url: routes.performanceReview.route.create({}),
      },
      { label: data?.me.feedback.feedbackCycle.Name ?? '' },
    ],
    loading,
  );

  const userInfo = useUserInfo();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false);

  const [updateObjectiveResults] = useUpdateObjectiveResults();

  const [submitFeedback] = useSubmitSelfPerformanceEvaluationMutation({
    awaitRefetchQueries: true,
    /* eslint-disable-next-line no-restricted-syntax */
    refetchQueries: [
      {
        query: MyPerformanceReviewsDocument,
      },
    ],
  });
  const errorHandler = useMutationErrorHandler();
  const [isFinishing, signAndFinish] = useEmployeeSignAndFinish();

  const isProcessing = isSubmitting || isSavingDraft || isFinishing;

  const [answersToSendOnServer, setAnswersToSendOnServer] = useState<
    UpdateEmployeeFeedbackAnswerInput[]
  >([]);

  const [goalResultsToSend, setGoalResultsToSend] = useState<
    GoalResultChange[]
  >([]);

  const isDirty =
    answersToSendOnServer.length > 0 || goalResultsToSend.length > 0;
  useUnsavedChanges(isDirty);

  const [goalsMode, setGoalsMode] = useState<GoalsInReviewMode>('NONE');
  const [allGoalResultsSet, setAllGoalResultsSet] = useState(true);

  const handleSubmitFeedback = useCallback(async () => {
    setIsSubmitting(true);

    const promises: Promise<unknown>[] = [
      submitFeedback({
        variables: {
          input: {
            id: feedbackId,
            answers: answersToSendOnServer,
            onlyUpdateAnswers: false,
          },
        },
      }),
    ];

    if (goalResultsToSend.length > 0) {
      promises.push(
        updateObjectiveResults({
          variables: {
            input: {
              results: goalResultsToSend.map((g) => ({
                objectiveId: g.objectiveId,
                predefinedResultOptionId: g.predefinedResultOptionId,
                result: g.result,
              })),
            },
          },
        }),
      );
    }

    const results = await Promise.allSettled(promises);
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length > 0) {
      errorHandler(failed[0].reason);
    } else {
      setAnswersToSendOnServer([]);
      setGoalResultsToSend([]);
    }
    setIsSubmitting(false);
  }, [
    submitFeedback,
    updateObjectiveResults,
    feedbackId,
    errorHandler,
    answersToSendOnServer,
    goalResultsToSend,
  ]);

  if (error) {
    return <ServerError />;
  }

  if (loading || data === undefined) {
    return <LoadingSelfPerformanceEvaluation />;
  }

  const feedback = data.me.feedback;

  const isSubmitted =
    feedback.flair__State__c === EmployeeFeedbackState.Submitted;
  const managerSubmitted =
    feedback.managerFeedback?.flair__State__c ===
    EmployeeFeedbackState.Submitted;
  const isCycleCompleted =
    feedback.feedbackCycle.flair__State__c !== FeedbackCycleState.InProgress;

  const isAccepted = feedback.outcome.flair__Accepted_At__c; //When the employee Complete or Sign and Finish

  if (isSubmitted && managerSubmitted) {
    return (
      <Redirect
        to={routes.pastPerformanceReview.route}
        feedbackId={feedbackId}
      />
    );
  }

  const {
    flair__Meeting_Required__c: meetingRequired,
    flair__Signature_required__c: signatureRequired,
  } = feedback.feedbackCycle;

  const signAndCompleteBtnLbl = t(
    signatureRequired ? 'signComplete' : 'complete',
  );
  // todo: add translations
  const saveChangesButtonLabel = isSubmitted
    ? t('saveChanges')
    : t('saveDraft');
  const saveChangesDescriptionLabel = isSubmitted
    ? t('unsavedChanges')
    : t('unsavedChangesDoNotForget');

  const handleFinish = () => {
    if (signatureRequired && satisfactionQuestion) {
      setShowSatisfactionModal(true);
    } else {
      return signAndFinish(feedback.outcome.Id);
    }
  };
  const satisfactionQuestion = feedback.answers.find(
    (item) => item.question.flair__Is_Satisfaction_Section__c,
  );

  const shouldShowSatisfactionConfirm =
    !isSubmitted && satisfactionQuestion && !signatureRequired;

  const handleCompleteFeedbackCycle = () => {
    if (shouldShowSatisfactionConfirm) {
      setShowSatisfactionModal(true);
    } else {
      handleSubmitFeedback();
    }
  };

  const handleAnswerToSave = (
    answerToSave: UpdateEmployeeFeedbackAnswerInput,
  ) => {
    setAnswersToSendOnServer((existent) => {
      if (existent.some((e) => e.id === answerToSave.id)) {
        return existent.map((e) =>
          e.id === answerToSave.id ? answerToSave : e,
        );
      } else {
        return [...existent, answerToSave];
      }
    });
  };

  const handleGoalResultChange = (change: GoalResultChange) => {
    setGoalResultsToSend((existing) => {
      const index = existing.findIndex(
        (e) => e.objectiveId === change.objectiveId,
      );
      if (index >= 0) {
        return existing.map((e, i) => (i === index ? change : e));
      }
      return [...existing, change];
    });
  };

  const handleSaveDraft = () => {
    setIsSavingDraft(true);

    const promises: Promise<unknown>[] = [
      submitFeedback({
        variables: {
          input: {
            id: feedbackId,
            answers: answersToSendOnServer,
            onlyUpdateAnswers: true,
          },
        },
      }),
    ];

    if (goalResultsToSend.length > 0) {
      promises.push(
        updateObjectiveResults({
          variables: {
            input: {
              results: goalResultsToSend.map((g) => ({
                objectiveId: g.objectiveId,
                predefinedResultOptionId: g.predefinedResultOptionId,
                result: g.result,
              })),
            },
          },
        }),
      );
    }

    Promise.allSettled(promises)
      .then((results) => {
        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length > 0) {
          errorHandler(failed[0].reason);
        } else {
          setAnswersToSendOnServer([]);
          setGoalResultsToSend([]);
        }
      })
      .finally(() => setIsSavingDraft(false));
  };

  const shouldRenderSignAndCompleteBtn =
    signatureRequired && isSubmitted && !isAccepted && !meetingRequired;

  return (
    <>
      <PageHeader title={feedback.feedbackCycle.Name} />
      {satisfactionQuestion && !isCycleCompleted && !isAccepted && (
        <EmployeeSatisfactionModal
          onSubmit={() =>
            signatureRequired
              ? signAndFinish(feedback.outcome.Id)
              : handleSubmitFeedback()
          }
          answer={satisfactionQuestion}
          visible={showSatisfactionModal}
          isSignStep={false}
          onClose={() => setShowSatisfactionModal(false)}
          isLoading={isSubmitting || isFinishing}
        />
      )}
      <Row>
        <Col
          xl={{ span: 8, order: 1 }}
          xs={{ span: 12, order: 2 }}
          style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 15.5rem)' }}>
          <FeedbackCycleIntroductionText
            feedbackCycle={feedback.feedbackCycle}
          />
          {/* // make readonly diring progress and if completed */}
          <EmployeeFeedbackQuestionnaire
            answers={feedback.answers.filter(visibleForEmployee)}
            setIsComplete={setIsComplete}
            onAnswerToSave={handleAnswerToSave}
            readOnly={isCycleCompleted || isProcessing}
            isPeerReviewer={false}
            questionsSettings={{
              ratingMin: feedback.feedbackCycle.flair__Rating_Min__c,
              ratingMax: feedback.feedbackCycle.flair__Rating_Max__c,
            }}
          />
          <EmployeeEvaluationInlineGoals
            feedbackCycle={data?.me.feedback.feedbackCycle}
            employeeId={userInfo.id}
            employeeName={userInfo.name}
            isManager={false}
            isEmployee={true}
            readOnly={isCycleCompleted}
            goalResultsToSend={goalResultsToSend}
            onChange={handleGoalResultChange}
            onAllGoalResultsSetChange={setAllGoalResultsSet}
            onGoalsModeChange={setGoalsMode}
          />
        </Col>

        <Col
          xl={{ span: 4, order: 2 }}
          xs={{ span: 12, order: 1 }}
          className="evaluation-sidebar">
          {goalsMode === 'SIDEBAR' && (
            <GoalsSectionWithContext
              employeeId={userInfo.id}
              employeeName={userInfo.name}
              objectiveTimePeriodId={
                feedback.feedbackCycle.objectiveTimePeriod?.Id
              }
            />
          )}

          {!meetingRequired && isSubmitted && signatureRequired && (
            <FeedbackStatusSection
              ended={false}
              readonly={false}
              signatureRequired={signatureRequired}
            />
          )}

          {shouldRenderSignAndCompleteBtn && (
            <Row className="submit-button">
              <Col className="text-end">
                <ConfirmButton
                  variant="primary"
                  className="mb-3 w-100"
                  disabled={isProcessing}
                  onConfirm={handleFinish}
                  isProcessing={isFinishing}
                  label={signAndCompleteBtnLbl}
                  confirmTitle={t('confirmTitle')}
                  confirmText={t('confirmText')}
                />
              </Col>
            </Row>
          )}

          {!isSubmitted && (
            <Row className="submit-button">
              <Col className="text-end">
                {isDirty && (
                  <SaveChangesButton
                    isProcessing={isSavingDraft}
                    disabled={isProcessing}
                    onClick={handleSaveDraft}
                    description={saveChangesDescriptionLabel}
                    label={saveChangesButtonLabel}
                  />
                )}
                {/*we will show a normal confirm dialog if no need to show the satisfaction question in this step*/}
                {!shouldShowSatisfactionConfirm && (
                  <ConfirmButton
                    variant="primary"
                    disabled={!isComplete || !allGoalResultsSet || isProcessing}
                    tooltip={
                      !isComplete
                        ? t('pleaseFillRequiredFields')
                        : !allGoalResultsSet
                        ? rootT(
                            'performanceReview.inlineGoalQuestion.allResultsRequired',
                          )
                        : undefined
                    }
                    className="mb-3 w-100"
                    onConfirm={handleSubmitFeedback}
                    isProcessing={isSubmitting}
                    label={t('submit')}
                    confirmTitle={t('confirmTitle')}
                    confirmText={t('confirmText')}
                  />
                )}
                {/* todo: IS it possible to update answers after employee submit or not? */}
                {shouldShowSatisfactionConfirm && (
                  <Button
                    disabled={!isComplete || !allGoalResultsSet}
                    tooltip={
                      !allGoalResultsSet
                        ? rootT(
                            'performanceReview.inlineGoalQuestion.allResultsRequired',
                          )
                        : undefined
                    }
                    className="mb-3 w-100"
                    label={t('submit')}
                    isProcessing={isSubmitting}
                    onClick={handleCompleteFeedbackCycle}
                  />
                )}
              </Col>
            </Row>
          )}
          {isSubmitted && isDirty && (
            <Row className="submit-button">
              <Col className="text-end">
                <SaveChangesButton
                  isProcessing={isSavingDraft}
                  disabled={isProcessing}
                  onClick={handleSaveDraft}
                  description={saveChangesDescriptionLabel}
                  label={saveChangesButtonLabel}
                />
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </>
  );
};

export const SelfPerformanceEvaluation: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { feedbackId } = useParams<Params>();

  return (
    <>
      <Row className="justify-content-center">
        <Col lg={12}>
          <Content feedbackId={feedbackId} />
        </Col>
      </Row>
    </>
  );
};
